/**
 * Pure kilometre / GPS math for billable driving distance and anti-tamper
 * validation. Generalised and cleaned from the original heuristic in
 * `services/gps-tracking.ts` (that file is left untouched).
 *
 * Kilometres are BILLED/REIMBURSED, so accuracy and anti-tamper matter:
 * mock/fake locations, implausible speed jumps and large time/distance gaps
 * are all flagged.
 */

import type { DriveSegment, GeoPoint, GeoStamp, TravelMode, TravelModeOverride } from './types'

const EARTH_RADIUS_KM = 6371

/** Hybrid driving detector: at/above this speed a segment is "driving". */
export const DRIVING_SPEED_THRESHOLD_KMH = 15

/** Above this speed a segment is physically implausible for a road trip. */
export const IMPLAUSIBLE_SPEED_KMH = 200

/** A gap longer than this between samples is suspicious (default 5 minutes). */
export const MAX_SAMPLE_GAP_MS = 5 * 60 * 1000

/** A single jump longer than this (km) without intermediate fixes is a gap. */
export const MAX_SEGMENT_DISTANCE_KM = 20

/**
 * Minimum summed distance for a run of consecutive driving segments to be
 * billable (km). GPS jitter while standing still produces isolated
 * "driving"-speed micro-segments (2026-10-07 field test: 0.16 km of billable
 * jitter — apparent 107 km/h — recorded during a session where the provider
 * never drove). A real drive is hundreds of consecutive segments spanning
 * kilometres; jitter runs never accumulate this much. A manual "I'm driving"
 * override on a segment keeps its run billable regardless.
 */
export const MIN_BILLABLE_DRIVE_RUN_KM = 0.2

function toRadians(degrees: number): number {
  return degrees * (Math.PI / 180)
}

/** Great-circle distance between two points in kilometres (haversine). */
export function haversineKm(from: GeoPoint, to: GeoPoint): number {
  const dLat = toRadians(to.lat - from.lat)
  const dLng = toRadians(to.lng - from.lng)
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(from.lat)) * Math.cos(toRadians(to.lat)) * Math.sin(dLng / 2) * Math.sin(dLng / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return EARTH_RADIUS_KM * c
}

/**
 * Speed in km/h between two timestamped samples. Returns 0 when the time delta
 * is non-positive (out-of-order or duplicate samples) so callers never divide
 * by zero or produce negative/Infinity speeds.
 */
export function speedKmh(from: GeoStamp, to: GeoStamp): number {
  const distanceKm = haversineKm(from, to)
  const timeDiffHours = (to.timestamp - from.timestamp) / (1000 * 60 * 60)
  if (timeDiffHours <= 0) return 0
  return distanceKm / timeDiffHours
}

export interface ClassifyOptions {
  /** Manual override applied by the worker for this segment. */
  manualOverride?: TravelModeOverride
  /** Override the driving speed threshold (km/h). */
  drivingThresholdKmh?: number
}

/**
 * Classify a segment as driving / walking / idle using the hybrid policy:
 * automatic speed threshold (~15 km/h) with a manual override that always wins.
 * Billable === driving (km that count toward reimbursement).
 */
export function classifySegment(from: GeoStamp, to: GeoStamp, options: ClassifyOptions = {}): DriveSegment {
  const distanceKm = haversineKm(from, to)
  const speed = speedKmh(from, to)
  const threshold = options.drivingThresholdKmh ?? DRIVING_SPEED_THRESHOLD_KMH
  const manualOverride = options.manualOverride ?? null

  let mode: TravelMode
  if (manualOverride === 'driving') {
    mode = 'driving'
  } else if (manualOverride === 'walking') {
    mode = 'walking'
  } else if (distanceKm === 0) {
    mode = 'idle'
  } else if (speed >= threshold) {
    mode = 'driving'
  } else {
    mode = 'walking'
  }

  return {
    from,
    to,
    distanceKm,
    speedKmh: speed,
    mode,
    manualOverride,
    billable: mode === 'driving',
  }
}

/** A per-segment manual override keyed by the segment's *start* index. */
export type SegmentOverrides = Record<number, TravelModeOverride>

export interface BuildSegmentsOptions {
  drivingThresholdKmh?: number
  /** Map of segment index → manual override (index = index of `from` sample). */
  overrides?: SegmentOverrides
}

/** Build classified drive segments from an ordered list of GPS samples. */
export function buildSegments(samples: GeoStamp[], options: BuildSegmentsOptions = {}): DriveSegment[] {
  const segments: DriveSegment[] = []
  for (let i = 0; i < samples.length - 1; i += 1) {
    segments.push(
      classifySegment(samples[i], samples[i + 1], {
        manualOverride: options.overrides?.[i] ?? null,
        drivingThresholdKmh: options.drivingThresholdKmh,
      }),
    )
  }
  return segments
}

/** Sum billable (driving) kilometres across already-classified segments. */
export function sumBillableDrivingKm(segments: DriveSegment[]): number {
  return segments.reduce((total, segment) => (segment.billable ? total + segment.distanceKm : total), 0)
}

export interface BillableSanityOptions {
  implausibleSpeedKmh?: number
  maxSampleGapMs?: number
  minDriveRunKm?: number
}

/**
 * Re-derive each segment's `billable` flag with evidence sanity applied.
 * Billed/reimbursed km must survive an audit, so distance that cannot be
 * verified from the track is excluded (the stored segments are untouched —
 * only the billable flags change):
 *
 * - **Teleports** — segments faster than {@link IMPLAUSIBLE_SPEED_KMH}. A stale
 *   cached fix followed by a fresh one reads as a multi-km hop in seconds
 *   (2026-10-06 field test: a 1.5 km segment at 1,500 km/h).
 * - **Untracked gaps** — segments spanning more than {@link MAX_SAMPLE_GAP_MS}.
 *   With the app backgrounded there are no intermediate fixes, so the straight
 *   line under-/over-states the real route (2026-10-06 field test: one 5.98 km
 *   segment covering a 14-minute backgrounded stretch). The distance may be
 *   real but is unverifiable; honest tracked-km is a lower bound.
 * - **Jitter runs** — see {@link MIN_BILLABLE_DRIVE_RUN_KM}. Runs containing a
 *   manual `driving` override are kept.
 *
 * Idempotent and pure: safe on client (live counter) and server (persisted
 * total) alike.
 */
export function applyBillableSanity(
  segments: DriveSegment[],
  options: BillableSanityOptions = {},
): DriveSegment[] {
  const implausibleSpeed = options.implausibleSpeedKmh ?? IMPLAUSIBLE_SPEED_KMH
  const maxGapMs = options.maxSampleGapMs ?? MAX_SAMPLE_GAP_MS
  const minRunKm = options.minDriveRunKm ?? MIN_BILLABLE_DRIVE_RUN_KM

  // Pass 1: per-segment plausibility (never billable when unverifiable).
  const result = segments.map((segment) => {
    const dtMs = segment.to.timestamp - segment.from.timestamp
    const plausible =
      segment.speedKmh <= implausibleSpeed && dtMs >= 0 && dtMs <= maxGapMs
    return { ...segment, billable: segment.mode === 'driving' && plausible }
  })

  // Pass 2: drop jitter runs — consecutive billable segments whose summed
  // distance never reaches a real drive.
  let runStart = -1
  let runKm = 0
  let runOverridden = false
  const closeRun = (endExclusive: number) => {
    if (runStart < 0) return
    if (runKm < minRunKm && !runOverridden) {
      for (let j = runStart; j < endExclusive; j++) result[j].billable = false
    }
    runStart = -1
    runKm = 0
    runOverridden = false
  }
  for (let i = 0; i < result.length; i++) {
    if (result[i].billable) {
      if (runStart < 0) runStart = i
      runKm += result[i].distanceKm
      if (result[i].manualOverride === 'driving') runOverridden = true
    } else {
      closeRun(i)
    }
  }
  closeRun(result.length)

  return result
}

/** Convenience: build segments from samples and sum billable driving km. */
export function computeBillableDrivingKm(samples: GeoStamp[], options: BuildSegmentsOptions = {}): number {
  return sumBillableDrivingKm(applyBillableSanity(buildSegments(samples, options)))
}

// --- Anti-tamper validation ---------------------------------------------------

export type TamperFlagType =
  | 'MOCK_LOCATION'
  | 'IMPLAUSIBLE_SPEED'
  | 'TIME_GAP'
  | 'DISTANCE_GAP'
  | 'OUT_OF_ORDER'

export interface TamperFlag {
  type: TamperFlagType
  /** Index of the offending sample (or the *end* sample of an offending pair). */
  index: number
  detail: string
}

export interface TamperOptions {
  implausibleSpeedKmh?: number
  maxSampleGapMs?: number
  maxSegmentDistanceKm?: number
}

export interface TamperReport {
  valid: boolean
  flags: TamperFlag[]
}

/**
 * Validate a track of GPS samples for tampering / data-quality problems:
 * - mock/fake-location fixes (`isMock`)
 * - implausible speed jumps (> ~200 km/h)
 * - large time gaps between fixes (default > 5 min)
 * - large distance jumps with no intermediate fixes (default > 20 km)
 * - out-of-order timestamps
 *
 * Returns every flag found; `valid` is true only when no flags are present.
 */
export function validateTrack(samples: GeoStamp[], options: TamperOptions = {}): TamperReport {
  const implausibleSpeed = options.implausibleSpeedKmh ?? IMPLAUSIBLE_SPEED_KMH
  const maxGapMs = options.maxSampleGapMs ?? MAX_SAMPLE_GAP_MS
  const maxDistanceKm = options.maxSegmentDistanceKm ?? MAX_SEGMENT_DISTANCE_KM
  const flags: TamperFlag[] = []

  samples.forEach((sample, index) => {
    if (sample.isMock) {
      flags.push({ type: 'MOCK_LOCATION', index, detail: 'Sample flagged as a mock/fake location fix' })
    }
  })

  for (let i = 0; i < samples.length - 1; i += 1) {
    const from = samples[i]
    const to = samples[i + 1]
    const dtMs = to.timestamp - from.timestamp

    if (dtMs < 0) {
      flags.push({
        type: 'OUT_OF_ORDER',
        index: i + 1,
        detail: `Sample ${i + 1} timestamp precedes sample ${i}`,
      })
      continue
    }

    if (dtMs > maxGapMs) {
      flags.push({
        type: 'TIME_GAP',
        index: i + 1,
        detail: `Gap of ${(dtMs / 1000).toFixed(0)}s exceeds ${(maxGapMs / 1000).toFixed(0)}s`,
      })
    }

    const distanceKm = haversineKm(from, to)
    if (distanceKm > maxDistanceKm) {
      flags.push({
        type: 'DISTANCE_GAP',
        index: i + 1,
        detail: `Jump of ${distanceKm.toFixed(1)}km exceeds ${maxDistanceKm}km without intermediate fixes`,
      })
    }

    const speed = speedKmh(from, to)
    if (speed > implausibleSpeed) {
      flags.push({
        type: 'IMPLAUSIBLE_SPEED',
        index: i + 1,
        detail: `Speed of ${speed.toFixed(0)}km/h exceeds ${implausibleSpeed}km/h`,
      })
    }
  }

  return { valid: flags.length === 0, flags }
}
